import { NonNegIntSchema } from "#schemas/primitives.ts";
import { domainRegistry } from "#schemas/registry.ts";
import { z } from "zod";

/**
 * Starter state ids — seeded into new projects as a default sprite-animation
 * skeleton. Not authoritative: state ids are user-defined strings at runtime.
 */
export const STATE_IDS = ["idle", "wait", "walk", "run", "attack", "die"] as const;

export const StateIdSchema = z.string().min(1).register(domainRegistry, {
  id: "state_id",
  title: "FSM state id",
  description: "Animation state machine identifier (user-defined string).",
  surface: "store",
  llmExposed: true,
  group: "fsm",
  examples: ["idle", "walk", "boss_intro"],
});
export type StateId = z.infer<typeof StateIdSchema>;

/** Inclusive [start, end] frame index pair; start <= end, both >= 0. */
export const FrameRangeSchema = z
  .tuple([NonNegIntSchema, NonNegIntSchema])
  .check((ctx) => {
    const [s, e] = ctx.value;
    if (s > e) {
      ctx.issues.push({
        code: "custom",
        message: `start ${s} > end ${e}`,
        input: ctx.value,
      });
    }
  })
  .register(domainRegistry, {
    id: "frame_range",
    title: "Frame range",
    description: "Inclusive [start, end] frame index pair.",
    surface: "store",
    llmExposed: true,
    group: "fsm",
    examples: [[0, 0], [3, 7]],
  });
export type FrameRange = z.infer<typeof FrameRangeSchema>;

export function frameRange(start: number, end: number): FrameRange {
  return FrameRangeSchema.parse([start, end]);
}

export const StateFrameRangesSchema = z
  .record(StateIdSchema, FrameRangeSchema)
  .register(domainRegistry, {
    id: "state_frame_ranges",
    title: "State frame ranges",
    description: "Per-state frame range map keyed by user-defined state id.",
    surface: "store",
    llmExposed: true,
    persistKey: "stateFrames",
    group: "fsm",
  });
export type StateFrameRanges = z.infer<typeof StateFrameRangesSchema>;
